import React from 'react';
import PropTypes from 'prop-types';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Button } from '../ui';

export default function TableToolbar({
  title,
  search,
  onSearchChange,
  searchPlaceholder,
  columns,
  data,
  filename,
  extraActions,
}) {
  const handleExportExcel = () => {
    try {
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, title);
      XLSX.writeFile(wb, `${filename || title}.xlsx`);
    } catch (err) {
      console.error('Export Excel error:', err);
    }
  };

  const handleExportPDF = () => {
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();

      doc.setFontSize(14);
      doc.text(title, 14, 15);
      doc.setFontSize(8);
      doc.text(`Généré le ${new Date().toLocaleDateString('fr-TN')}`, 14, 22);
      doc.line(14, 25, pageW - 14, 25);

      const rows = data.map((row) => columns.map((col) => {
        const val = col.accessor ? row[col.accessor] : '-';
        if (typeof val === 'number') return val.toLocaleString('fr-TN', { minimumFractionDigits: 3 });
        return val ?? '-';
      }));

      autoTable(doc, {
        head: [columns.map((c) => c.label)],
        body: rows,
        startY: 28,
        styles: { fontSize: 7, cellPadding: 1.5 },
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontSize: 8, halign: 'center' },
        didDrawPage: (dataArg) => {
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(7);
          doc.text(`Page ${dataArg.pageNumber} / ${pageCount}`, pageW - 30, doc.internal.pageSize.getHeight() - 8);
          doc.text(title, 14, doc.internal.pageSize.getHeight() - 8);
        },
      });

      doc.save(`${filename || title}.pdf`);
    } catch (err) {
      console.error('Export PDF error:', err);
    }
  };

  const handlePrint = () => {
    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) return;

      const tableHead = columns.map((c) => `<th style="text-align:${c.align || 'left'}">${c.label}</th>`).join('');
      const tableBody = data.map((row, i) => {
        const cells = columns.map((col) => {
          const val = col.accessor ? row[col.accessor] : '-';
          const formatted = typeof val === 'number'
            ? val.toLocaleString('fr-TN', { minimumFractionDigits: 3 })
            : (val ?? '-');
          return `<td style="text-align:${col.align || 'left'}">${formatted}</td>`;
        }).join('');
        return `<tr${i % 2 === 0 ? ' class="even"' : ''}>${cells}</tr>`;
      }).join('');

      printWindow.document.write(`
        <html>
        <head>
          <title>${title}</title>
          <style>
            @page { size: landscape; margin: 15mm; }
            * { box-sizing: border-box; }
            body {
              font-family: 'Segoe UI', Arial, sans-serif;
              font-size: 9px;
              color: #1f2937;
              margin: 0; padding: 0;
            }
            .print-header {
              display: flex; justify-content: space-between; align-items: center;
              border-bottom: 2px solid #3b82f6; padding-bottom: 8px; margin-bottom: 12px;
            }
            .print-header h1 { font-size: 16px; margin: 0; color: #1e40af; }
            .print-header .date { font-size: 9px; color: #6b7280; }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; }
            thead th {
              background: #3b82f6; color: white; padding: 6px 8px; font-size: 8px;
              font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;
            }
            tbody td { padding: 5px 8px; border-bottom: 1px solid #e5e7eb; }
            tbody tr.even { background: #f9fafb; }
            .print-footer {
              position: fixed; bottom: 10mm; left: 15mm; right: 15mm;
              text-align: center; font-size: 7px; color: #9ca3af;
              border-top: 1px solid #e5e7eb; padding-top: 4px;
            }
            .no-print { display: none !important; }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h1>${title}</h1>
            <span class="date">${new Date().toLocaleDateString('fr-TN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
          </div>
          <table><thead><tr>${tableHead}</tr></thead><tbody>${tableBody}</tbody></table>
          <div class="print-footer">Document g�n�r� depuis AccountInTime</div>
          <script>window.onload = function() { window.print(); window.close(); }</script>
        </body>
        </html>
      `);
      printWindow.document.close();
    } catch (err) {
      console.error('Print error:', err);
    }
  };

  const handleExportTemplate = () => {
    const template = [{
      Nom: '',
      SKU: '',
      Référence: '',
      Description: '',
      Unité: 'pcs',
      'Prix achat': 0,
      'Prix vente': 0,
      Qté: 0,
      'Stock min': 0,
    }];
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Modèle');
    XLSX.writeFile(wb, 'modele_import_articles.xlsx');
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-4 no-print">
      <div className="flex-1 min-w-[200px]">
        {onSearchChange && (
          <input
            type="text"
            placeholder={searchPlaceholder || 'Rechercher...'}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {extraActions}
        <Button variant="secondary" size="sm" onClick={handleExportTemplate}>
          Modèle
        </Button>
        <Button variant="success" size="sm" onClick={handleExportExcel}>
          Excel
        </Button>
        <Button variant="danger" size="sm" onClick={handleExportPDF}>
          PDF
        </Button>
        <Button variant="secondary" size="sm" onClick={handlePrint}>
          Imprimer
        </Button>
      </div>
    </div>
  );
}

TableToolbar.propTypes = {
  title: PropTypes.string.isRequired,
  search: PropTypes.string,
  onSearchChange: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    accessor: PropTypes.string,
    align: PropTypes.string,
  })).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  filename: PropTypes.string,
  extraActions: PropTypes.node,
};